// ============================================================
// AdminCostPage — M12.B (PRD §7.5 AC-DASH-1..3 + §6.1.K R-P0-K1..K8)
// ============================================================
// 4 tab:
//   - overview:   3 card (hôm nay · tháng này · tháng trước) + banner ngân sách
//                 + split theo task (ask_ai / chat_ai / grade_exam) + quick-look top 5 NV
//   - chart:      Line chart 30 ngày (stacked ask_ai / chat_ai / grade_exam) — R-P0-K2
//   - top-users:  Period selector (tuần/tháng/quý) + bảng top NV — R-P0-K3
//   - history:    Bảng Lịch sử AI có filter (task/provider/NV/search/date) + pagination — R-P0-K8
//
// Format VN: dd/MM/yyyy HH:mm · toLocaleString('vi-VN') + 'đ'. USD hiển thị 4 chữ số thập phân.
// ============================================================

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts';
import { useAuthStore } from '../lib/auth.js';
// M146 (2026-05-12): manager với quyền 'cost' được vào page
import { hasPermission } from '../lib/permissions.js';
import { api } from '../lib/api.js';

// ---------- Helpers ----------
function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return iso; }
}
function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('vi-VN', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}
function fmtVnd(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return Number(v).toLocaleString('vi-VN') + 'đ';
}
function fmtUsd(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  if (n === 0) return '$0';
  if (Math.abs(n) < 0.0001) return '$<0.0001';
  return '$' + n.toFixed(4);
}
function shortDate(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return iso || '';
  return iso.slice(5); // MM-DD
}

// M98-2 (2026-04-26): rename label rules_m80 → "Học nội quy" cho dễ hiểu.
// M133 (2026-04-27): tách rules_m80 thành 2 dòng riêng (Sinh / Chấm) trong "Lịch sử AI".
//   - rules_m80       (legacy): row cũ trước migration 051 (1 row/bộ, model = sonnet, cost gộp).
//                                Daily breakdown vẫn dùng key này → không đổi line chart.
//   - rules_m80_gen   (mới):    Sinh 5 câu (Sonnet) — 1 row mỗi bộ
//   - rules_m80_grade (mới):    Chấm 1 câu (Haiku)  — N row mỗi bộ
const TASK_LABEL = {
  ask_ai:               'Hỏi đáp',
  chat_ai:              'Chat AI',
  grade_exam:           'Chấm thi + Nội quy',
  grade_rules_exam:     'Chấm nội quy (cũ)',
  embedding_standalone: 'Embed riêng',
  rules_m80:            'Học nội quy',
  rules_m80_gen:        'Sinh câu Nội quy',
  rules_m80_grade:      'Chấm Nội quy',
};
const TASK_COLOR = {
  ask_ai:               '#3b82f6', // blue-500
  chat_ai:              '#10b981', // emerald-500
  grade_exam:           '#f59e0b', // amber-500
  grade_rules_exam:     '#06b6d4', // cyan-500
  embedding_standalone: '#a855f7', // purple-500
  rules_m80:            '#ec4899', // pink-500 (legacy)
  rules_m80_gen:        '#ec4899', // pink-500 (giữ cùng tone với rules_m80)
  rules_m80_grade:      '#f472b6', // pink-400 (sáng hơn để phân biệt)
};

// ============================================================
// Tab 1: Overview — AC-DASH-1 + AC-DASH-3
// ============================================================
function OverviewTab() {
  const [summary, setSummary] = useState(null);
  const [split, setSplit] = useState(null);
  const [topUsers, setTopUsers] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const [s, sp, tu] = await Promise.all([
        api.costSummary(),
        api.costSplit(),
        api.costTopUsers({ period: 'month', limit: 5 }),
      ]);
      setSummary(s);
      setSplit(sp);
      setTopUsers(tu);
    } catch (e) {
      toast.error('Lỗi tải tổng quan: ' + (e?.message || e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-slate-500 p-4">Đang tải…</div>;
  if (!summary) return <div className="text-red-600 p-4">Không tải được dữ liệu.</div>;

  const b = summary.budget;
  const pct = b?.pct ?? 0;
  const bannerCls =
    b?.status === 'critical' ? 'bg-rose-50 border-rose-300 text-rose-800' :
    b?.status === 'warning'  ? 'bg-amber-50 border-amber-300 text-amber-800' :
                               'bg-emerald-50 border-emerald-300 text-emerald-800';
  const bannerIcon = b?.status === 'critical' ? '🚨' : b?.status === 'warning' ? '⚠️' : '✅';
  const bannerMsg =
    b?.monthly_vnd <= 0
      ? 'Chưa đặt ngân sách tháng (cấu hình tại Admin → AI → Provider → monthly_budget_usd).'
      : b?.status === 'critical' ? `Đã vượt ngân sách tháng (${pct}% — tạm dừng AI nếu đã set auto-shutoff).`
      : b?.status === 'warning'  ? `Sắp chạm ngưỡng ngân sách tháng (${pct}%).`
      :                            `Chi tiêu tháng đang trong ngân sách (${pct}%).`;

  return (
    <div className="space-y-4">
      {/* Banner ngân sách */}
      <div className={`rounded-lg border px-4 py-3 flex items-center justify-between ${bannerCls}`}>
        <div className="flex items-center gap-2">
          <span className="text-2xl">{bannerIcon}</span>
          <div>
            <div className="font-semibold">{bannerMsg}</div>
            {b?.monthly_vnd > 0 && (
              <div className="text-xs opacity-80">
                Đã dùng <strong>{fmtVnd(b.used_vnd)}</strong> / ngân sách <strong>{fmtVnd(b.monthly_vnd)}</strong>
              </div>
            )}
          </div>
        </div>
        <button onClick={load} className="text-sm underline hover:no-underline">⟳ Tải lại</button>
      </div>

      {/* 3 card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <CostCard
          title="Hôm nay"
          subtitle={summary.today.window}
          vnd={summary.today.vnd}
          usd={summary.today.usd}
          color="blue"
          split={summary.today.split}
        />
        <CostCard
          title="Tháng này"
          subtitle={summary.this_month.window}
          vnd={summary.this_month.vnd}
          usd={summary.this_month.usd}
          color="indigo"
          split={summary.this_month.split}
          budget={b?.monthly_vnd}
          pct={pct}
        />
        <CostCard
          title="Tháng trước"
          subtitle={summary.last_month.window}
          vnd={summary.last_month.vnd}
          usd={summary.last_month.usd}
          color="slate"
        />
      </div>

      {/* Split theo task + provider (R-P0-K4) */}
      <section className="bg-white rounded-lg border p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-800">Chia theo task × provider (tháng này)</h3>
          <span className="text-xs text-slate-500">{split?.from} → {split?.to}</span>
        </div>
        {!split?.items?.length ? (
          <div className="text-sm text-slate-500 py-6 text-center">Chưa có chi tiêu AI nào tháng này.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b">
                  <th className="px-2 py-2 text-left text-xs font-semibold">Task</th>
                  <th className="px-2 py-2 text-left text-xs font-semibold">Provider</th>
                  <th className="px-2 py-2 text-right text-xs font-semibold">Số request</th>
                  <th className="px-2 py-2 text-right text-xs font-semibold">Chi phí (VND)</th>
                  <th className="px-2 py-2 text-right text-xs font-semibold">Chi phí (USD)</th>
                </tr>
              </thead>
              <tbody>
                {split.items.map((it, i) => (
                  <tr key={i} className="border-b hover:bg-slate-50">
                    <td className="px-2 py-2">
                      <span
                        className="inline-block w-2 h-2 rounded-full mr-2 align-middle"
                        style={{ backgroundColor: TASK_COLOR[it.task] }}
                      />
                      {TASK_LABEL[it.task] || it.task}
                    </td>
                    <td className="px-2 py-2 font-mono text-xs">{it.provider}</td>
                    <td className="px-2 py-2 text-right">{it.count}</td>
                    <td className="px-2 py-2 text-right font-semibold">{fmtVnd(it.cost_vnd)}</td>
                    <td className="px-2 py-2 text-right text-slate-500 text-xs">{fmtUsd(it.cost_usd)}</td>
                  </tr>
                ))}
                {/* Totals per task */}
                {split.by_task?.length > 1 && split.by_task.map((t, i) => (
                  <tr key={'tot-' + i} className="bg-slate-50 font-semibold">
                    <td className="px-2 py-2">Σ {TASK_LABEL[t.task] || t.task}</td>
                    <td className="px-2 py-2 text-slate-400 text-xs italic">all providers</td>
                    <td className="px-2 py-2 text-right">{t.count}</td>
                    <td className="px-2 py-2 text-right">{fmtVnd(t.cost_vnd)}</td>
                    <td className="px-2 py-2 text-right text-slate-500 text-xs">{fmtUsd(t.cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Top users preview */}
      <section className="bg-white rounded-lg border p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-800">Top 5 NV chi nhiều nhất (tháng này)</h3>
          <span className="text-xs text-slate-500">{topUsers?.from} → {topUsers?.to}</span>
        </div>
        {!topUsers?.items?.length ? (
          <div className="text-sm text-slate-500 py-6 text-center">Chưa có dữ liệu.</div>
        ) : (
          <ol className="space-y-1">
            {topUsers.items.map((u, i) => (
              <li key={u.employee_id || i} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-slate-50">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="font-mono text-xs text-slate-400 w-6">{i + 1}.</span>
                  <span className="truncate font-medium">{u.full_name}</span>
                  <span className="text-xs text-slate-500">· {u.shop_name || '—'}</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xs text-slate-500">{u.request_count} req</span>
                  <span className="font-semibold">{fmtVnd(u.cost_vnd)}</span>
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}

function CostCard({ title, subtitle, vnd, usd, color, split, budget, pct }) {
  const palette = {
    blue:   'bg-blue-50   border-blue-200   text-blue-800',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
    slate:  'bg-slate-50  border-slate-200  text-slate-700',
  }[color] || 'bg-slate-50 border-slate-200 text-slate-700';
  return (
    <div className={`rounded-lg border p-4 ${palette}`}>
      <div className="flex items-baseline justify-between">
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-xs opacity-70 font-mono">{subtitle}</div>
      </div>
      <div className="mt-2 text-2xl font-bold">{fmtVnd(vnd)}</div>
      <div className="text-xs opacity-70">{fmtUsd(usd)}</div>
      {budget !== undefined && budget > 0 && (
        <div className="mt-2">
          <div className="h-2 bg-white/60 rounded overflow-hidden">
            <div
              className={pct >= 100 ? 'bg-rose-500' : pct >= 80 ? 'bg-amber-500' : 'bg-emerald-500'}
              style={{ width: Math.min(pct, 100) + '%', height: '100%' }}
            />
          </div>
          <div className="text-[11px] mt-1 opacity-80">{pct}% ngân sách ({fmtVnd(budget)})</div>
        </div>
      )}
      {split && (
        <div className="mt-3 space-y-0.5 text-[11px] opacity-80">
          <div>• Hỏi đáp: {fmtVnd(split.ask_ai?.vnd)}</div>
          <div>• Chat AI: {fmtVnd(split.chat_ai?.vnd)}</div>
          <div>• Chấm thi + Nội quy: {fmtVnd(split.grade_exam?.vnd)}</div>
          <div>• Chấm nội quy (cũ): {fmtVnd(split.grade_rules_exam?.vnd)}</div>
          <div>• Học nội quy: {fmtVnd(split.rules_m80?.vnd)}</div>
          <div>• Embed riêng: {fmtVnd(split.embedding_standalone?.vnd)}</div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Tab 2: Chart 30 ngày — R-P0-K2
// ============================================================
function ChartTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  async function load(override) {
    setLoading(true);
    try {
      const r = await api.costChart(override || { from, to });
      setData(r);
      if (!from) setFrom(r.from);
      if (!to)   setTo(r.to);
    } catch (e) {
      toast.error('Lỗi tải biểu đồ: ' + (e?.message || e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load({}); }, []);

  const chartData = useMemo(() => {
    if (!data?.days) return [];
    return data.days.map(d => ({
      date: shortDate(d.date),
      'Hỏi đáp': d.ask_ai_vnd,
      'Chat AI': d.chat_ai_vnd,
      'Chấm thi + Nội quy': d.grade_exam_vnd,
      'Chấm nội quy': d.grade_rules_exam_vnd || 0,
      'Học nội quy': d.rules_m80_vnd || 0,
      'Embed riêng': d.embedding_standalone_vnd || 0,
      total: d.total_vnd,
    }));
  }, [data]);

  const totalRange = useMemo(() => {
    if (!data?.days) return 0;
    return data.days.reduce((s, d) => s + d.total_vnd, 0);
  }, [data]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-sm">
          Từ:{' '}
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          />
        </label>
        <label className="text-sm">
          Đến:{' '}
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          />
        </label>
        <button
          onClick={() => load({ from, to })}
          className="px-3 py-1 rounded bg-brand-600 hover:bg-brand-700 text-white text-sm"
        >
          Áp dụng
        </button>
        <button
          onClick={() => { setFrom(''); setTo(''); load({}); }}
          className="px-3 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm"
        >
          Reset 30 ngày
        </button>
        {data && (
          <span className="ml-auto text-sm text-slate-600">
            Tổng khoảng: <strong>{fmtVnd(totalRange)}</strong>
          </span>
        )}
      </div>

      {loading ? (
        <div className="text-slate-500 p-4">Đang tải biểu đồ…</div>
      ) : !chartData.length ? (
        <div className="text-slate-500 p-8 text-center border rounded">Không có dữ liệu.</div>
      ) : (
        <div className="bg-white border rounded p-4" style={{ height: 400 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => {
                  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + 'tr';
                  if (v >= 1_000) return (v / 1_000).toFixed(0) + 'k';
                  return String(v);
                }}
              />
              <Tooltip formatter={(v) => fmtVnd(v)} />
              <Legend />
              <Line type="monotone" dataKey="Hỏi đáp"       stroke={TASK_COLOR.ask_ai}               strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="Chat AI"      stroke={TASK_COLOR.chat_ai}              strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="Chấm thi + Nội quy" stroke={TASK_COLOR.grade_exam}     strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="Chấm nội quy" stroke={TASK_COLOR.grade_rules_exam}     strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="Học nội quy" stroke={TASK_COLOR.rules_m80}             strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="Embed riêng"  stroke={TASK_COLOR.embedding_standalone} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Tab 3: Top users — R-P0-K3
// ============================================================
function TopUsersTab() {
  // M28.A (2026-04-24): 5 preset + custom date range.
  // period=custom → dùng customFrom + customTo (YYYY-MM-DD).
  const [period, setPeriod] = useState('month');
  const [limit, setLimit] = useState(10);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo,   setCustomTo]   = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const payload = period === 'custom'
        ? { period: 'month', limit, from: customFrom, to: customTo }  // period thắng bởi from/to
        : { period, limit };
      const r = await api.costTopUsers(payload);
      setData(r);
    } catch (e) {
      toast.error('Lỗi tải top users: ' + (e?.message || e));
    } finally {
      setLoading(false);
    }
  }

  // Auto-reload khi preset đổi (trừ custom — chờ bấm Áp dụng)
  useEffect(() => {
    if (period !== 'custom') load();
    /* eslint-disable-next-line */
  }, [period, limit]);

  // Lần đầu load
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const PERIODS = [
    { k: 'today',     label: 'Hôm nay' },
    { k: 'yesterday', label: 'Hôm qua' },
    { k: 'week',      label: 'Tuần này' },
    { k: 'month',     label: 'Tháng này' },
    { k: 'quarter',   label: 'Quý này' },
    { k: 'custom',    label: 'Tuỳ chọn…' },
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1 border rounded-lg overflow-hidden">
          {PERIODS.map(p => (
            <button
              key={p.k}
              onClick={() => setPeriod(p.k)}
              className={`px-3 py-1.5 text-sm ${period === p.k
                ? 'bg-brand-600 text-white'
                : 'bg-white hover:bg-slate-50'}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <label className="text-sm">
          Số lượng:{' '}
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="border rounded px-2 py-1 text-sm"
          >
            <option value={5}>Top 5</option>
            <option value={10}>Top 10</option>
            <option value={20}>Top 20</option>
            <option value={50}>Top 50</option>
          </select>
        </label>
        {data && period !== 'custom' && (
          <span className="ml-auto text-xs text-slate-500">{data.from} → {data.to}</span>
        )}
      </div>

      {/* M28.A Date range custom — chỉ hiện khi chọn "Tuỳ chọn…" */}
      {period === 'custom' && (
        <div className="flex items-end gap-2 flex-wrap bg-slate-50 border rounded-lg p-3">
          <label className="text-xs text-slate-600 flex flex-col">
            Từ
            <input
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="border rounded px-2 py-1 text-sm mt-0.5"
            />
          </label>
          <label className="text-xs text-slate-600 flex flex-col">
            Đến
            <input
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              className="border rounded px-2 py-1 text-sm mt-0.5"
            />
          </label>
          <button
            onClick={load}
            disabled={!customFrom || !customTo || loading}
            className="px-4 py-1.5 rounded bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-40"
          >
            {loading ? 'Đang tải…' : 'Áp dụng'}
          </button>
          {data && (
            <span className="ml-auto text-xs text-slate-500 pb-1">Đang xem: {data.from} → {data.to}</span>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-slate-500 p-4">Đang tải…</div>
      ) : !data?.items?.length ? (
        <div className="text-slate-500 p-8 text-center border rounded">Chưa có chi tiêu AI nào trong khoảng này.</div>
      ) : (
      <div className="space-y-3">
        {/* M28 (2026-04-24): bar chart ngang — ai là người tiêu tốn nhất */}
        <div className="bg-white border rounded-lg p-3">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-sm font-semibold text-slate-700">Chi phí AI theo nhân viên (VND)</div>
            <div className="text-xs text-slate-400">Top {data.items.length} · {data.from} → {data.to}</div>
          </div>
          <ResponsiveContainer width="100%" height={Math.max(200, data.items.length * 36 + 40)}>
            <BarChart
              data={data.items.map((u) => ({
                name: u.full_name + (u.actor_type === 'admin' ? ' 🛡' : ''),
                shop: u.actor_type === 'admin' ? 'Admin (Chat AI)' : (u.shop_name || '—'),
                cost_vnd: u.cost_vnd,
                requests: u.request_count,
                tokens: u.total_tokens,
                actor_type: u.actor_type,
              }))}
              layout="vertical"
              margin={{ top: 10, right: 40, bottom: 10, left: 12 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis
                type="number"
                tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(v))}
                tick={{ fontSize: 11, fill: '#64748b' }}
              />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 12, fill: '#334155' }}
                width={140}
              />
              <Tooltip
                formatter={(value, name, props) => {
                  if (name === 'cost_vnd') return [fmtVnd(value), 'Chi phí'];
                  return [value, name];
                }}
                labelFormatter={(n, payload) => {
                  const p = payload?.[0]?.payload;
                  return p ? `${p.name} · ${p.shop}` : n;
                }}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const p = payload[0].payload;
                  return (
                    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-2 text-xs">
                      <div className="font-semibold text-slate-800">{p.name}</div>
                      <div className="text-slate-500">{p.shop}</div>
                      <div className="mt-1 text-brand-700 font-semibold">{fmtVnd(p.cost_vnd)}</div>
                      <div className="text-slate-500">{p.requests} request · {p.tokens?.toLocaleString('vi-VN')} tokens</div>
                    </div>
                  );
                }}
              />
              <Bar dataKey="cost_vnd" radius={[0, 4, 4, 0]}>
                {data.items.map((_, i) => {
                  // Top 1/2/3 highlight vàng/bạc/đồng; còn lại brand blue
                  const color =
                    i === 0 ? '#f59e0b' :   // gold
                    i === 1 ? '#94a3b8' :   // silver
                    i === 2 ? '#d97706' :   // bronze
                    '#3b82f6';              // blue
                  return <Cell key={i} fill={color} />;
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-500"></span>🥇 Top 1</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-slate-400"></span>🥈 Top 2</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-600"></span>🥉 Top 3</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-blue-500"></span>Còn lại</span>
          </div>
        </div>

        <div className="overflow-x-auto border rounded">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-2 py-2 text-left text-xs font-semibold">#</th>
                <th className="px-2 py-2 text-left text-xs font-semibold">Nhân viên</th>
                <th className="px-2 py-2 text-left text-xs font-semibold">Shop</th>
                <th className="px-2 py-2 text-right text-xs font-semibold">Số request</th>
                <th className="px-2 py-2 text-right text-xs font-semibold">Tokens</th>
                <th className="px-2 py-2 text-right text-xs font-semibold">Chi phí (VND)</th>
                <th className="px-2 py-2 text-right text-xs font-semibold">Chi phí (USD)</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((u, i) => {
                // M28 (2026-04-24): key unique cho cả NV (employee) lẫn admin
                const rowKey = `${u.actor_type || 'e'}-${u.actor_id ?? u.employee_id ?? i}`;
                const isAdmin = u.actor_type === 'admin';
                return (
                <tr key={rowKey} className={`border-b hover:bg-slate-50 ${i < 3 ? 'font-medium' : ''}`}>
                  <td className="px-2 py-2 font-mono text-xs text-slate-400">
                    {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}
                  </td>
                  <td className="px-2 py-2">
                    <span>{u.full_name}</span>
                    {isAdmin && (
                      <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 border border-indigo-200">
                        ADMIN
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-slate-600">
                    {isAdmin ? (
                      <span className="text-indigo-600 italic text-xs">Chat AI (admin)</span>
                    ) : (u.shop_name || '—')}
                  </td>
                  <td className="px-2 py-2 text-right">{u.request_count}</td>
                  <td className="px-2 py-2 text-right text-slate-500 text-xs">{u.total_tokens?.toLocaleString('vi-VN')}</td>
                  <td className="px-2 py-2 text-right font-semibold">{fmtVnd(u.cost_vnd)}</td>
                  <td className="px-2 py-2 text-right text-slate-500 text-xs">{fmtUsd(u.cost_usd)}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
}

// ============================================================
// Tab 4: History — R-P0-K8 (filter + pagination)
// ============================================================
function HistoryTab() {
  const [filters, setFilters] = useState({
    task: '',
    provider: '',
    employee_id: '',
    shop_id: '',
    from: '',
    to: '',
    search: '',
  });
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // id_src của row đang mở

  async function load(overridePage) {
    setLoading(true);
    try {
      const p = overridePage ?? page;
      const params = { page: p, size };
      for (const [k, v] of Object.entries(filters)) {
        if (v !== '' && v !== null && v !== undefined) params[k] = v;
      }
      const r = await api.costHistory(params);
      setData(r);
      if (overridePage !== undefined) setPage(overridePage);
    } catch (e) {
      toast.error('Lỗi tải lịch sử: ' + (e?.message || e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(1); /* eslint-disable-next-line */ }, [size]);

  function applyFilter() { load(1); }
  function resetFilter() {
    setFilters({ task: '', provider: '', employee_id: '', shop_id: '', from: '', to: '', search: '' });
    setTimeout(() => load(1), 0);
  }

  const totalPages = data?.total_pages || 1;

  return (
    <div className="space-y-3">
      {/* Filters */}
      <div className="bg-slate-50 border rounded p-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
          <label>
            Task
            <select
              value={filters.task}
              onChange={(e) => setFilters(f => ({ ...f, task: e.target.value }))}
              className="w-full border rounded px-2 py-1 mt-0.5"
            >
              <option value="">Tất cả</option>
              <option value="ask_ai">Hỏi đáp</option>
              <option value="chat_ai">Chat AI</option>
              <option value="grade_exam">Chấm thi + Nội quy</option>
              <option value="grade_rules_exam">Chấm nội quy (cũ)</option>
              <option value="rules_m80">Học nội quy (tất cả)</option>
              <option value="rules_m80_gen">└ Sinh câu Nội quy</option>
              <option value="rules_m80_grade">└ Chấm Nội quy</option>
              <option value="embedding_standalone">Embed riêng</option>
            </select>
          </label>
          <label>
            Provider
            <input
              value={filters.provider}
              onChange={(e) => setFilters(f => ({ ...f, provider: e.target.value }))}
              className="w-full border rounded px-2 py-1 mt-0.5"
              placeholder="openai / anthropic / …"
            />
          </label>
          <label>
            Employee ID
            <input
              type="number"
              value={filters.employee_id}
              onChange={(e) => setFilters(f => ({ ...f, employee_id: e.target.value }))}
              className="w-full border rounded px-2 py-1 mt-0.5"
              placeholder="#"
            />
          </label>
          <label>
            Shop ID
            <input
              type="number"
              value={filters.shop_id}
              onChange={(e) => setFilters(f => ({ ...f, shop_id: e.target.value }))}
              className="w-full border rounded px-2 py-1 mt-0.5"
              placeholder="#"
            />
          </label>
          <label>
            Từ ngày
            <input
              type="date"
              value={filters.from}
              onChange={(e) => setFilters(f => ({ ...f, from: e.target.value }))}
              className="w-full border rounded px-2 py-1 mt-0.5"
            />
          </label>
          <label>
            Đến ngày
            <input
              type="date"
              value={filters.to}
              onChange={(e) => setFilters(f => ({ ...f, to: e.target.value }))}
              className="w-full border rounded px-2 py-1 mt-0.5"
            />
          </label>
          <label className="col-span-2">
            Tìm trong câu hỏi / trả lời
            <input
              value={filters.search}
              onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') applyFilter(); }}
              className="w-full border rounded px-2 py-1 mt-0.5"
              placeholder="từ khoá…"
            />
          </label>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <button
            onClick={applyFilter}
            className="px-3 py-1 rounded bg-brand-600 hover:bg-brand-700 text-white text-sm"
          >
            Lọc
          </button>
          <button
            onClick={resetFilter}
            className="px-3 py-1 rounded bg-slate-200 hover:bg-slate-300 text-slate-700 text-sm"
          >
            Xoá bộ lọc
          </button>
          <span className="ml-auto text-xs text-slate-500">
            {data ? `${data.total.toLocaleString('vi-VN')} bản ghi` : ''}
          </span>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-slate-500 p-4">Đang tải…</div>
      ) : !data?.items?.length ? (
        <div className="text-slate-500 p-8 text-center border rounded">Không có bản ghi khớp bộ lọc.</div>
      ) : (
        <div className="overflow-x-auto border rounded">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-2 py-2 text-left text-xs font-semibold">Thời gian</th>
                <th className="px-2 py-2 text-left text-xs font-semibold">Task</th>
                <th className="px-2 py-2 text-left text-xs font-semibold">Provider · Model</th>
                <th className="px-2 py-2 text-left text-xs font-semibold">Ai dùng</th>
                <th className="px-2 py-2 text-right text-xs font-semibold">Tokens</th>
                <th className="px-2 py-2 text-right text-xs font-semibold">Chi phí</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((r) => {
                const isOpen = expanded === r.id_src;
                return (
                  <>
                    <tr
                      key={r.id_src}
                      className={`border-b hover:bg-slate-50 cursor-pointer ${isOpen ? 'bg-blue-50' : ''}`}
                      onClick={() => setExpanded(isOpen ? null : r.id_src)}
                    >
                      <td className="px-2 py-2 whitespace-nowrap font-mono text-xs">{fmtDateTime(r.created_at)}</td>
                      <td className="px-2 py-2">
                        <span
                          className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle"
                          style={{ backgroundColor: TASK_COLOR[r.task] }}
                        />
                        {TASK_LABEL[r.task] || r.task}
                      </td>
                      <td className="px-2 py-2 font-mono text-xs">
                        {r.provider} · <span className="text-slate-500">{r.model || '—'}</span>
                      </td>
                      <td className="px-2 py-2">
                        {r.employee_name || r.user_display || <span className="text-slate-400 italic">—</span>}
                        {r.shop_name && <span className="text-xs text-slate-500 block">{r.shop_name}</span>}
                      </td>
                      <td className="px-2 py-2 text-right text-xs font-mono">
                        {r.tokens_in}→{r.tokens_out}
                      </td>
                      <td className="px-2 py-2 text-right font-semibold">{fmtVnd(r.cost_vnd)}</td>
                      <td className="px-2 py-2 text-slate-400 text-xs">{isOpen ? '▾' : '▸'}</td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-blue-50/50 border-b">
                        <td colSpan={7} className="px-4 py-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                              <div className="text-xs font-semibold text-slate-500 mb-1">Câu hỏi</div>
                              <div className="whitespace-pre-wrap break-words bg-white rounded p-2 border text-slate-700 max-h-48 overflow-auto">
                                {r.question_text || <em className="text-slate-400">(không có)</em>}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs font-semibold text-slate-500 mb-1">Trả lời</div>
                              <div className="whitespace-pre-wrap break-words bg-white rounded p-2 border text-slate-700 max-h-48 overflow-auto">
                                {r.answer_text || <em className="text-slate-400">(không có)</em>}
                              </div>
                            </div>
                          </div>
                          <div className="mt-2 text-xs text-slate-500 flex gap-4 flex-wrap">
                            <span>ID: <span className="font-mono">{r.id_src}</span></span>
                            <span>USD: <span className="font-mono">{fmtUsd(r.cost_usd)}</span></span>
                            {r.case_type && <span>Loại: <span className="font-mono">{r.case_type}</span></span>}
                            {r.employee_id && <span>Employee #{r.employee_id}</span>}
                            {r.shop_id && <span>Shop #{r.shop_id}</span>}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {data && data.total > 0 && (
        <div className="flex items-center justify-between text-sm">
          <div className="text-slate-500">
            Trang {data.page} / {totalPages} · {size}/trang
          </div>
          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => load(1)}
              className="px-2 py-1 rounded border disabled:opacity-40"
            >«</button>
            <button
              disabled={page <= 1}
              onClick={() => load(page - 1)}
              className="px-2 py-1 rounded border disabled:opacity-40"
            >‹</button>
            <input
              type="number"
              min={1}
              max={totalPages}
              value={page}
              onChange={(e) => setPage(Number(e.target.value) || 1)}
              onKeyDown={(e) => { if (e.key === 'Enter') load(Number(e.currentTarget.value) || 1); }}
              className="w-14 border rounded px-2 py-1 text-center"
            />
            <button
              disabled={page >= totalPages}
              onClick={() => load(page + 1)}
              className="px-2 py-1 rounded border disabled:opacity-40"
            >›</button>
            <button
              disabled={page >= totalPages}
              onClick={() => load(totalPages)}
              className="px-2 py-1 rounded border disabled:opacity-40"
            >»</button>
            <select
              value={size}
              onChange={(e) => setSize(Number(e.target.value))}
              className="border rounded px-2 py-1"
            >
              <option value={20}>20/trang</option>
              <option value={50}>50/trang</option>
              <option value={100}>100/trang</option>
              <option value={200}>200/trang</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Main page
// ============================================================
export default function AdminCostPage() {
  const user = useAuthStore(s => s.user);
  const [tab, setTab] = useState('overview');

  if (!hasPermission(user, 'cost')) {
    return <div className="p-6 text-red-600">403 — bạn không có quyền xem trang này.</div>;
  }

  const TABS = [
    { k: 'overview',  label: 'Tổng quan' },
    { k: 'chart',     label: 'Biểu đồ 30 ngày' },
    { k: 'topusers',  label: 'Top nhân viên' },
    { k: 'history',   label: 'Lịch sử AI' },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-700 shrink-0">← Trang admin</Link>
          <h1 className="text-base md:text-lg font-semibold truncate">Chi phí AI</h1>
          <span className="text-xs text-slate-400 hidden md:inline">M12.B · PRD §7.5 · R-P0-K1..K8</span>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-3 md:p-4">
        {/* M148c: tabs scroll ngang trên mobile */}
        <div className="flex gap-1 mb-4 border-b overflow-x-auto scrollbar-hide -mx-3 px-3 md:mx-0 md:px-0">
          {TABS.map(t => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`tap-feedback shrink-0 px-3 md:px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                tab === t.k
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-slate-600 hover:text-slate-800'}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-3 md:p-4">
          {tab === 'overview' && <OverviewTab />}
          {tab === 'chart'    && <ChartTab />}
          {tab === 'topusers' && <TopUsersTab />}
          {tab === 'history'  && <HistoryTab />}
        </div>
      </main>
    </div>
  );
}
